import React from 'react';
import GuideHero from 'components/guide-hero';
import {
  CitationBlock,
  CitationBlockMini,
  CitationExampleBox,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  IntextCitation,
  TextWithColor,
  SingleSpacedList,
} from 'components/guide';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';
import Footer from 'components/footer';

const AmaGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in AMA Format"
      imgSrc={require('images/ama-modal.png')}
      imgAlt="Laptop with screenshot on screen of manual for AMA format"
    >
      <p>
        Welcome to our concise AMA citation guide designed to assist you in
        achieving accuracy and consistency in you medical research papers.
      </p>
      <p>
        In our digital age, citation machines can simplify your work. This guide
        includes tips on how to effectively use these citation generators to
        automatically create AMA citations, saving you valuable time. Yet, we
        developed an extensive AMA referencing manual for you to understand the
        principles of AMA citation style.
      </p>
      <p>
        By empowering you to create citations by hand, we hope to help you
        enhance your research credibility and avoid common citation errors!
      </p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton text="MLA Guide" href="/mla" variant="purple" />
        <GuideLinkButton text="APA Guide" href="/apa" variant="blue" />
        <GuideLinkButton
          text="Chicago Guide"
          href="/chicago"
          variant="orange"
        />
      </GuideLinkButtonSection>

      <FormatOverview />
      <InTextCitations />
      <Bibliography />
      <BookCitations />
      <JournalCitations />
      <WebsiteCitations />
      <BookChapterCitations />
      <NewspaperAndMagazineCitations />
      <ConferencePaperCitations />
      <ThesisDissertationCitations />
      <SocialMediaCitations />
      <DrugCitations />
      <DrugMonographs />
      <PatentCitations />
      <FigureGraphPictureCitations />
      <PersonalCommunicationCitations />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const FormatOverview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>AMA Format Overview</GuideHeader>

    <p>
      The American Medical Association (AMA) citation style is often used in the
      field of medicine and other health-related disciplines. It maintains a
      specific structure for both in-text citations and the reference list to
      standardize the academic writing process.
    </p>
    <p>
      The AMA referencing style is a prominent citation method, especially
      within the medical community. It establishes a uniform set of guidelines
      for in-text citations and reference lists, fostering clarity and
      consistency in scholarly writing. By conforming to these norms, you can
      increase the comprehension of your work and facilitate the process for
      others to find and cite your research.
    </p>
    <p>
      <strong>In-text citations</strong> in the AMA format are represented by
      concise numerical references within the body of your paper. These numbers
      are presented in superscript and correspond to the order of appearance in
      the text.
    </p>
    <p>
      This numerical approach, unique to AMA style, provides a straightforward
      and streamlined way to guide readers to the corresponding entries in the
      reference list.
    </p>
    <p>
      <strong>The reference list</strong> in AMA format is an exhaustive
      enumeration of all the sources cited in your work, listed in numerical
      order, reflecting the sequence in which they were cited in the text. It is
      crucial to include pertinent details such as authors&apos; last names and
      initials, article title, journal abbreviation, volume number, issue
      number, page range, and DOI if available.
    </p>
    <p>
      By familiarizing yourself with and adhering to the AMA citation
      guidelines, you can construct a well-organized, easy-to-follow paper that
      meets the rigorous standards of the medical and health sciences fields.
      Whether you&apos;re a student, researcher, or professional, the AMA style
      offers a systematic approach to referencing that upholds the integrity of
      your work.
    </p>
  </GuideSection>
);

const InTextCitations: React.FC = () => (
  <GuideSection id="InText">
    <GuideHeader>Create In-Text Citations in AMA</GuideHeader>
    <p>
      The AMA citation style emphasizes simplicity and precision in its in-text
      citation practice, making use of superscript numbers. These numerical
      references lead readers to the corresponding citations in the reference
      list and serve to authenticate the information used in the research.
    </p>
    <p>Here&apos;s how the AMA style manages in-text citations:</p>
    <p>
      <strong>Basic in-text citation:</strong> In AMA, the standard in-text
      citation is a sequential superscript number, appearing at the end of the
      sentence, outside the period. For example:{' '}
      <IntextCitation>
        The findings were consistent with previous research^1
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Sequential order:</strong> The numbers follow a sequence based on
      the order of citation within the text. If you cite the same source again,
      use the same number. For example:{' '}
      <IntextCitation>
        This study^2 aligns with the earlier research^1 on the subject
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Multiple sources in one sentence:</strong> When more than one
      source is cited in the same sentence, the superscript numbers are
      separated by commas without spaces. For example:{' '}
      <IntextCitation>
        The research concluded that both factors were relevant^3,4
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Direct quotations:</strong> Though not commonly used in scientific
      writing, direct quotes are cited with the page number in the reference
      list but not in the in-text citation. For example:{' '}
      <IntextCitation>
        One study found, &quot;The results were conclusive&quot;^5
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Citing online and audiovisual sources:</strong> The same
      sequential numbering is applied to all types of sources, including online
      publications and audiovisual materials. For example:{' '}
      <IntextCitation>
        Recent online data suggests a new trend in patient care^6. This concept
        was also explored in the documentary^7
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Indirect sources:</strong> If you refer to a source cited in
      another work, the reference list should include the secondary source you
      have read. For example:{' '}
      <IntextCitation>
        The theory has been further explored by other authors^8
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Special cases:</strong> In situations like citing personal
      communications or unpublished works, these are not listed in the reference
      list but may be cited in the text.{' '}
      <IntextCitation>For example: According to Dr. Johnson ^9</IntextCitation>
      ...
    </p>
    <p>
      The AMA citation style&apos;s clear and concise numerical system
      facilitates a smooth reading experience and accurate referencing. By using
      this consistent and orderly method, you contribute to the credibility of
      your scholarly work, aiding readers in locating your sources and verifying
      the research.
    </p>
    <p>
      The AMA style&apos;s focus on numbers over names or dates simplifies the
      in-text citation process, making it a preferred choice for many in the
      medical and scientific fields.
    </p>
  </GuideSection>
);

const Bibliography: React.FC = () => (
  <GuideSection id="Bibliography">
    <GuideHeader>Format Your AMA Bibliography</GuideHeader>
    <p>
      The AMA referencing style requires a comprehensive &quot;References&quot;
      section, which includes all of the works you&apos;ve consulted and cited
      in your paper. This reference list serves as a beacon for your readers to
      find and verify the sources you have used, thus upholding the integrity of
      your research.
    </p>
    <p>
      Here&apos;s how to create a reference list according to AMA guidelines:
    </p>
    <ul>
      <li>
        <strong>Positioning and title:</strong> The reference list should be
        initiated on a separate page at the end of your paper. The heading
        &quot;References&quot; should be left-aligned at the top of the page,
        without any special text formatting like bold or italics.
      </li>
      <li>
        <strong>Spacing and indentation:</strong> Use double line spacing
        throughout your reference list. All margins should be set at 1 inch. The
        first line of each citation should be left-aligned, while any subsequent
        lines are indented by half an inch.
      </li>
      <li>
        <strong>Sequence of entries:</strong> Unlike the alphabetic sorting in
        other styles, AMA requires you to list the references in the order they
        appear in the text, correlating to the numeric in-text citations.
      </li>
      <li>
        <strong>Entry structure:</strong> AMA has distinct formats for diverse
        sources such as books, journal articles, or websites. Nevertheless, most
        references will include elements like author names, the title of the
        work, the year and place of publication, and publisher details, where
        applicable.
      </li>
      <li>
        <strong>Punctuation and capitalization:</strong> The AMA style is
        specific about punctuation use and capitalization in references. Titles
        typically use sentence case, meaning only the first word of the title
        and any subtitles (and proper nouns) are capitalized. Always
        double-check for accurate and consistent punctuation use.
      </li>
      <li>
        <strong>Accuracy check:</strong> As a final step, ensure the accuracy of
        your references. Check spelling, punctuation, formatting, and the proper
        use of italics and capitalization.
      </li>
    </ul>
    <img
      src={require('images/ama-reference-list.png')}
      alt="Screenshot of an AMA reference list"
    />
    <p>
      By adhering to the AMA style in your reference list, you&apos;re
      maintaining consistency and reliability in your work, thereby making it
      easier for other researchers to engage with your writing. This way, the
      AMA referencing style serves as an efficient bridge connecting your
      research to the wider academic dialogue in the medical field.
    </p>
  </GuideSection>
);

const BookCitations: React.FC = () => (
  <GuideSection id="Book">
    <GuideHeader>Cite Books in AMA</GuideHeader>
    <p>
      When you&apos;re navigating the vast terrain of medical knowledge, books
      often become your map and compass. Citing them correctly in AMA style
      isn&apos;t just a formality; it&apos;s a respectful nod to the authors
      whose work you&apos;re building upon. Whether it&apos;s a medical treatise
      or a comprehensive textbook, knowing how to reference a book in AMA style
      is key for any aspiring medical scholar.
    </p>
    <p>
      Let&apos;s embark on this literary expedition and explore the nuances of
      book citation in AMA style.
    </p>
    <CitationBlock header="Citing a book in AMA">
      Author&apos;s Last Name First Initials. Title of Book. Publisher City,
      State: Publisher Name; Year Published.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J. Cardiology Essentials. New York, NY: Medtext Publishers; 2017.
    </CitationExampleBox>
    <p>
      <strong>Edited books:</strong> When citing an edited book in AMA, the
      format slightly changes:
    </p>
    <CitationExampleBox>
      Johnson L, ed. Neurology Handbook. Chicago, IL: Brainwave Press; 2018.
    </CitationExampleBox>
    <p>
      <strong>Multiple authors or editors:</strong> When generating an AMA
      citation for a book with 2 to 6 authors or editors, list all the authors:
    </p>
    <CitationExampleBox>
      Miller T, Adams D. Pharmacology Guide. Boston, MA: Pharmaco Press; 2015.
    </CitationExampleBox>
    <p>
      <strong>Chapters in edited books:</strong> If you want to cite a specific
      chapter, the format is:
    </p>
    <CitationExampleBox>
      Harris M, Thompson R. Epilepsy Diagnosis. In: Johnson L, ed. Neurology
      Handbook. Chicago, IL: Brainwave Press; 2018: 40-55.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        <strong>Electronic books:</strong> Include the URL and the accessed
        date.
      </li>
      <li>
        <strong>Multiple volumes:</strong> Mention the volume number you are
        citing.
      </li>
      <li>
        <strong>No author:</strong> Begin the citation with the book title.
      </li>
    </SingleSpacedList>
    <p>
      Citing books in AMA style may seem complex at first, but it&apos;s all
      about knowing the right format for the specific type of book you&apos;re
      citing. With these guidelines, you&apos;re well-equipped to cite anything
      from a groundbreaking medical research book to a chapter in an edited
      collection.
    </p>
    <p>
      Remember, the accuracy of your citations reflects the rigor of your work,
      so always pay attention to the details. For more specific situations or
      further guidance, don&apos;t hesitate to consult the latest AMA Manual of
      Style or our expert citation machine Bibcitation.com.
    </p>
  </GuideSection>
);
const JournalCitations: React.FC = () => (
  <GuideSection id="Journal">
    <GuideHeader>Cite Journal Articles in AMA</GuideHeader>
    <p>
      In the constantly evolving field of medicine, journal articles are the
      heartbeat that drives new discoveries and insights. They are the pulse of
      medical scholarship, conveying the latest research, studies, and findings.
      Citing them accurately in AMA style is an essential skill for every
      medical professional, researcher, and student.{' '}
    </p>
    <p>
      So, fasten your intellectual seat belts as we delve into the exciting
      world of journal article citation in AMA style!
    </p>
    <CitationBlock header="Citing a journal article in AMA">
      Author(s). Title of article. Abbreviated Journal Name. Year;volume(issue
      number):pages.
    </CitationBlock>
    <p>
      <strong>Example:</strong>
    </p>
    <CitationExampleBox>
      Taylor AB, Johnson KL. New Insights into Cardiac Rehabilitation. J Cardiol
      Rehab. 2019;35(4):200-210.
    </CitationExampleBox>
    <p>
      <strong>Multiple authors:</strong> For citing articles with 2 to 6
      authors, list all authors:
    </p>
    <CitationExampleBox>
      Smith A, Brown B, Johnson C, et al. Advances in Neurosurgery Techniques.
      Neurosurg Focus. 2020;48(3):e7.
    </CitationExampleBox>
    <p>
      <strong>Online journal articles:</strong> To generate AMA citations for
      journal articles available on the web, include the URL and the accessed
      date:
    </p>
    <CitationExampleBox>
      Author&apos;s Last Name First Initials. Title of article. Abbreviated
      Journal Name. Year;volume(issue number):pages. URL. Accessed Month Day,
      Year.
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> If you want to cite a journal article without
      an author, the format is:
    </p>
    <CitationExampleBox>
      Title of article. Abbreviated Journal Name. Year;volume(issue
      number):pages.
    </CitationExampleBox>
    <p>
      Citing journal articles in AMA style may seem like a medical puzzle, but
      it&apos;s a puzzle worth solving. Understanding the intricacies and
      variations is key to an accurate and professional citation. From standard
      journal articles to online and supplemental issues, these guidelines
      provide a sturdy framework for referencing the latest medical scholarship.
    </p>
    <p>
      For every twist and turn in AMA citation, these examples are your guide,
      ensuring that you always stay on the right path. Explore further with the
      AMA Manual of Style or seek the expertise of our citation generator
      Bibcitation.com.
    </p>
  </GuideSection>
);

const WebsiteCitations: React.FC = () => (
  <GuideSection id="Website">
    <GuideHeader>Cite Websites in AMA</GuideHeader>
    <p>
      The internet is like the central nervous system of modern information, and
      websites are the neurons that connect and transmit knowledge. In medicine
      and healthcare, reliable websites are treasure troves of valuable
      insights, data, and research. But how do you give credit to these digital
      sources in your scholarly work?
    </p>
    <p>
      Buckle up, and join us as we explore the intricate art of citing websites
      in AMA style.
    </p>
    <CitationBlock header="Citing a website in AMA">
      Author(s) if available. Title of the specific item cited. Name of website.
      URL. Accessed Month Day, Year.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Centers for Disease Control and Prevention. COVID-19 Vaccine Guidelines.
      CDC Website.{' '}
      https://www.cdc.gov/coronavirus/2019-ncov/vaccines/recommendations.html.
      Accessed April 20, 2021.
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> If no author is available, simply omit it from
      your citation and begin the entry with the title of the paper:
    </p>
    <CitationExampleBox>
      Understanding Hypertension. American Heart Association Website.{' '}
      https://www.heart.org/hypertension. Accessed February 14, 2022.
    </CitationExampleBox>
    <p>
      <strong>Webpage with a specific author:</strong> If the webpage you are
      citing has a specific author, include the author&apos;s name in your AMA
      reference:
    </p>
    <CitationExampleBox>
      Thompson J. The Future of Telemedicine. MedTech Today Website.{' '}
      https://www.medtech.com/telemedicine-future . Accessed October 5, 2020.
    </CitationExampleBox>
    <p>
      <strong>Citing an entire website:</strong> If citing an entire website
      (not a specific document or section), it&apos;s generally sufficient to
      include the URL within the text and not in the reference list.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <IntextCitation>
      Visit the World Health Organization&apos;s website for global health data
      (<a href="www.who.int">www.who.int</a>).
    </IntextCitation>
    <p>
      In the bustling online marketplace of ideas, citing websites in AMA style
      is like holding a compass that points you toward scholarly integrity.
      Whether you&apos;re citing a government report, a medical guideline, or an
      insightful blog post, these guidelines are your trusty roadmap.
      They&apos;ll help you acknowledge your digital sources with precision and
      style.
    </p>
    <p>
      With the AMA Manual of Style in one hand and these examples in the other,
      you&apos;re well-equipped to navigate the digital healthscape. Connect
      with the experts at Bibcitation.com for even more tailored assistance, and
      keep surfing the waves of online knowledge responsibly!
    </p>
  </GuideSection>
);

const BookChapterCitations: React.FC = () => (
  <GuideSection id="BookChapter">
    <GuideHeader>Cite Book Chapters in AMA</GuideHeader>
    <p>
      In the colossal body of medical literature, book chapters are akin to
      specialized cells, each performing a vital function in the organism of
      knowledge. Every chapter offers a unique perspective, contribution, or
      analysis, and deserves to be acknowledged with care and precision. But how
      do you go about doing that?
    </p>
    <p>
      Let&apos;s dissect the fascinating world of citing book chapters in AMA
      style, with a surgeon&apos;s attention to detail.
    </p>
    <CitationBlock header="Citing a book chapter in AMA">
      Author(s) of chapter. Title of chapter. In: Editor(s). Book title.
      Edition. City, State (or Country): Publisher; Year:page range.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J, Jones M. The Role of Genetics in Cancer. In: Thompson K, ed.
      Oncology Uncovered. 3rd ed. New York, NY: Medical Press; 2021:165-189.
    </CitationExampleBox>
    <p>
      <strong>Multiple authors and editors.</strong> If there are multiple
      authors and/or editors in your AMA citation, list them accordingly:
    </p>
    <CitationExampleBox>
      Brown A, Green B, White C. Heart Failure in Pediatrics. In: Black D, Grey
      E, eds. Pediatric Cardiology. 2nd ed. London, UK: Health Publishers;
      2019:345-367.
    </CitationExampleBox>
    <p>
      <strong>Chapter without specific author.</strong> If the chapter
      doesn&apos;t have a specific author, the citation would begin with the
      title:
    </p>
    <CitationExampleBox>
      Management of Type 2 Diabetes. In: Nelson P, ed. Endocrinology Essentials.
      5th ed. Boston, MA: Academic Books; 2020:123-142.
    </CitationExampleBox>
    <p>
      <strong>Online book chapter.</strong> For an online book chapter, the
      citation would include the URL and access date:
    </p>
    <CitationExampleBox>
      Harris R. Pathology of Infectious Diseases. In: Clark S, ed. Microbiology
      for Clinicians. 4th ed. Chicago, IL: eMedBooks; 2018:67-89.
      www.emedbooks.com/pathology-infectious-diseases. Accessed June 10, 2022.
    </CitationExampleBox>
    <p>
      Citing book chapters in AMA style is akin to carefully labeling the
      specimens in a lab - every detail matters, and precision is key. These
      examples illustrate the diversity and specificity required to acknowledge
      the multifaceted contributions within the vast library of medical
      literature. Whether it&apos;s a print or online chapter, single or
      multiple authors, the guidelines outlined here are your microscope,
      allowing you to zoom in and capture every nuance.
    </p>
  </GuideSection>
);

const NewspaperAndMagazineCitations: React.FC = () => (
  <GuideSection id="NewspaperMagazine">
    <GuideHeader>Cite Newspapers & Magazines in AMA</GuideHeader>
    <p>
      Newspaper and magazine articles offer a heartbeat to the ever-changing
      world of information, often providing the latest insights, discoveries, or
      controversies in the medical field. In other words, they are the EKG of
      current knowledge.
    </p>
    <p>
      But how do you accurately reflect these sources in your medical research
      or paper? Let&apos;s explore the vital signs of citing newspaper and
      magazine articles in AMA style.
    </p>
    <CitationBlock header="Citing a newspaper article in AMA">
      Author(s). Title of article. Name of Newspaper. Date of
      publication;Section:page range (column number).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Johnson A. Advances in Cardiac Surgery. The New York Times. March 15,
      2021;Health:A12.
    </CitationExampleBox>
    <p>
      <strong>No Author? No Problem!</strong> In some cases, an article may not
      have an identifiable author. In this situation, you would start the
      citation with the title:
    </p>
    <CitationExampleBox>
      New Vaccine Shows Promise in Trials. The Washington Post. May 10,
      2022;Science:B4.
    </CitationExampleBox>
    <p>
      <strong>Chapter without specific author.</strong> If the chapter
      doesn&apos;t have a specific author, the citation would begin with the
      title:
    </p>
    <CitationExampleBox>
      Management of Type 2 Diabetes. In: Nelson P, ed. Endocrinology Essentials.
      5th ed. Boston, MA: Academic Books; 2020:123-142.
    </CitationExampleBox>
    <p>
      <strong>Online book chapter.</strong> For an online book chapter, the
      citation would include the URL and access date:
    </p>
    <CitationExampleBox>
      Harris R. Pathology of Infectious Diseases. In: Clark S, ed. Microbiology
      for Clinicians. 4th ed. Chicago, IL: eMedBooks; 2018:67-89.
      www.emedbooks.com/pathology-infectious-diseases. Accessed June 10, 2022.
    </CitationExampleBox>
    <p>
      <strong>Cite a magazine article in AMA referencing style:</strong>
    </p>
    <CitationBlockMini>
      Author(s). Title of article. Name of Magazine. Date of publication; Volume
      (issue): page range.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Davis B, Smith C. The Mental Health Crisis in America. Time Magazine. June
      2021;237(6):30-45.
    </CitationExampleBox>
    <p>
      <strong>Online newspaper and magazine articles</strong>
    </p>
    <p>Citing an online newspaper or magazine article? Here&apos;s how:</p>
    <CitationBlockMini>
      Author(s). Title of article. Name of Newspaper or Magazine. Date of
      publication. URL. Accessed Month Day, Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Walker D. Understanding Alzheimer&apos;s Disease. Scientific American.
      July 12, 2021.
      https://www.scientificamerican.com/article/understanding-alzheimers.
      Accessed August 8, 2022.
    </CitationExampleBox>
    <p>
      Newspaper and magazine articles may appear to be fleeting, like heartbeats
      on a monitor, but they remain an essential component of the informational
      circulatory system. Whether in print or online, with or without an author,
      their citation in AMA style reflects the rhythmic and dynamic nature of
      current knowledge. As you explore the pulse of the latest medical
      discoveries, trends, or controversies, remember that these guidelines are
      the stethoscope to your academic precision.
    </p>
  </GuideSection>
);

const ConferencePaperCitations: React.FC = () => (
  <GuideSection id="ConferencePaper">
    <GuideHeader>Cite Conference Papers in AMA</GuideHeader>
    <p>
      In the bustling world of medical academia, conference papers are like
      exclusive symposiums - they capture the latest insights, innovations, and
      hypotheses. These nuggets of scholarly brilliance provide a forum for
      intellectual discourse, often long before the information finds its way
      into formal publications. So how do you translate this dynamic information
      into your research while adhering to AMA style? Let&apos;s embark on a
      journey through the vibrant world of conference paper citations.
    </p>
    <CitationBlock header="Citing a conference paper in AMA">
      Author(s). Title of paper. In: Editor(s), ed(s). Title of Conference
      Proceedings; Date of conference; Place of conference. Place of
      publication: Publisher; Year of publication: page range.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Thompson L, Williams R. Innovative Approaches to Cardiac Care. In: Johnson
      M, ed. Proceedings of the 12th Annual Cardiology Conference; April 5-7,
      2020; Chicago, IL. New York, NY: Medical Publishers; 2020: 123-130.
    </CitationExampleBox>
    <p>
      <strong>Conference papers from online proceedings</strong>
      Online conference proceedings call for slight adjustments to the citation:
    </p>
    <CitationExampleBox>
      Wilson G, James P. Managing Chronic Pain Through Alternative Therapies.
      In: Davis K, ed. International Pain Management Symposium; October 15-17,
      2021; London, UK. Boston, MA: Health Science Press; 2021: 290-300.
      https://www.painsymposium.org/proceedings. Accessed February 10, 2023.
    </CitationExampleBox>
    <p>
      <strong>Chapter without specific author</strong>
      If the chapter doesn&apos;t have a specific author, the citation would
      begin with the title:
    </p>
    <CitationExampleBox>
      Management of Type 2 Diabetes. In: Nelson P, ed. Endocrinology Essentials.
      5th ed. Boston, MA: Academic Books; 2020:123-142.
    </CitationExampleBox>
    <p>
      <strong>No Editor?</strong> In the absence of an editor, your AMA citation
      should begin with the title of the conference paper:
    </p>
    <CitationExampleBox>
      Exploring New Frontiers in Diabetes Treatment. In: 10th Annual Diabetes
      Research Conference; June 20-22, 2019; San Francisco, CA. Atlanta, GA:
      Medical Innovators Press; 2019: 50-60.
    </CitationExampleBox>
    <p>
      Conference papers are the crossroads of innovation and discovery,
      reflecting the intellectual exuberance of the medical community. They are
      fleeting glimpses into the future of medicine, accessible through various
      formats, be it in print or online. So the next time you tap into this
      wellspring of knowledge, let these guidelines be your compass, guiding you
      through the maze of scholarly excellence.
    </p>
  </GuideSection>
);

const SocialMediaCitations: React.FC = () => (
  <GuideSection id="SocialMedia">
    <GuideHeader>Cite Social Media Posts in AMA</GuideHeader>
    <p>
      In our digial age, social media has become more than just a platform for
      friends, families, and celebrities. It&apos;s now a space where scholars
      and scientists can share insights and discoveries. Social media posts,
      tweets, and updates have found their way into academic writing, reflecting
      a fascinating convergence of the formal and informal. In the AMA
      referencing style, the citation of these posts mirrors the vibrant,
      dynamic nature of the digital world. Let&apos;s explore how to properly
      cite these unique sources, bringing the voices of the virtual world into
      the scholarly conversation.
    </p>
    <CitationBlock header="Citing a social media post in AMA">
      Author(s). Title of thesis or dissertation [thesis/dissertation]. Name of
      institution awarding degree; Year of completion.
    </CitationBlock>
    <p>
      <strong>Citing a tweet in AMA.</strong> Twitter, with its concise and
      often impactful messages, has become a platform for professionals to share
      insights. Here&apos;s how to cite a tweet:
    </p>
    <CitationExampleBox>
      @DrJaneDoe. Exploring the relationship between nutrition and mental
      well-being in my latest study [Tweet]. Twitter. Published June 2, 2022.
      https://twitter.com/DrJaneDoe/status/123456789.
    </CitationExampleBox>
    <p>
      <strong>Citing a Facebook post in AMA.</strong> Facebook allows for longer
      posts, and scholars often use it to discuss their work. Here&apos;s how to
      cite a Facebook post:
    </p>
    <CitationExampleBox>
      Dr. John Smith. New insights into heart disease prevention unveiled at the
      recent conference. Facebook. Published April 15, 2022.
      https://www.facebook.com/DrJohnSmith/posts/987654321.
    </CitationExampleBox>
    <p>
      <strong>Citing an Instagram post in AMA.</strong> Instagram posts, with
      their visual appeal, can be relevant in certain contexts. Here&apos;s the
      format for citing an Instagram post:
    </p>
    <CitationExampleBox>
      @ScientistSarah. My team at work in the lab, analyzing new samples
      [Instagram photo]. Published May 1, 2021.
      https://www.instagram.com/p/ABCDEFG.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        If the social media handle or name is not available, you can use the
        description in the account&apos;s bio or the first 40 characters of the
        post.
      </li>
      <li>
        If the post is removed or the account becomes private, include a note in
        the citation indicating that the content was accessed at the time of
        citation.
      </li>
    </SingleSpacedList>
    <p>
      Citing social media posts in AMA style is a dance between the formality of
      academic writing and the immediacy of digital communication. It&apos;s a
      recognition that knowledge isn&apos;t confined to dusty libraries or elite
      conferences but thrives in the energetic, diverse, and democratic spaces
      of the internet. By understanding how to cite these posts, you&apos;re not
      only adhering to the rules of academic integrity but also embracing a
      broader, more connected understanding of knowledge.
    </p>
    <p>
      Whether a tweet, a Facebook update, or an Instagram photo, these citations
      capture the pulse of our times, where academia meets the everyday in a
      vibrant, never-ending conversation.
    </p>
  </GuideSection>
);

const ThesisDissertationCitations: React.FC = () => (
  <GuideSection id="ThesisDissertation">
    <GuideHeader>Cite Theses &amp; Dissertations in AMA</GuideHeader>
    <p>
      From a researcher&apos;s first tottering steps into the world of academia
      to the crowning achievement of a doctoral degree, theses and dissertations
      are milestones that mark significant intellectual growth and discovery. In
      the AMA referencing style, crediting these important documents requires
      meticulous attention to detail. Let&apos;s delve into the labyrinthine
      corridors of scholarly citation and learn how to pay proper homage to
      these monuments of academic achievement.
    </p>
    <CitationBlock header="Citing a thesis or dissertation in AMA">
      Author(s). Title of thesis or dissertation [thesis/dissertation]. Name of
      institution awarding degree; Year of completion.
    </CitationBlock>
    <p>
      <strong>Example of an AMA citation for a Master&apos;s Thesis:</strong>
    </p>
    <CitationExampleBox>
      Rogers L. The Interplay Between Diet and Mental Health [thesis].
      University of Chicago; 2018.
    </CitationExampleBox>
    <p>
      <strong>Example of an AMA citation for a Doctoral Dissertation:</strong>
    </p>
    <CitationExampleBox>
      Lee K. Novel Approaches to Cancer Immunotherapy [dissertation]. Harvard
      University; 2020.
    </CitationExampleBox>
    <p>
      <strong>Citing an online thesis or dissertation.</strong> With the advent
      of digital repositories, many theses and dissertations are now available
      online. The citation for an online document would include a URL:
    </p>
    <CitationExampleBox>
      Smith A. The Role of Genetics in Cardiovascular Diseases [dissertation].
      Stanford University; 2019.{' '}
      https://www.stanfordrepository.org/thesis/123456. Accessed March 3, 2023.
    </CitationExampleBox>
    <p>
      Theses and dissertations are the lighthouses of academia, guiding others
      toward new insights and understandings. They are the embodiment of
      persistence, innovation, and scholastic rigor. In citing them, one is not
      only acknowledging the work of a fellow scholar but also connecting with a
      lineage of knowledge and exploration. Remember, the journey of a thousand
      miles begins with a single citation, and there&apos;s no adventure too
      daunting for the well-prepared researcher!
    </p>
  </GuideSection>
);

const DrugMonographs: React.FC = () => (
  <GuideSection id="DrugMonographs">
    <GuideHeader>Cite Drug Monographs in AMA</GuideHeader>
    <p>
      In the medical field, a drug monograph serves as a detailed standard or
      protocol, outlining everything from the drug&apos;s active ingredients to
      its clinical applications. It&apos;s a treasure trove of information for
      healthcare professionals and researchers, and it often plays a crucial
      role in scholarly writing.
    </p>
    <p>
      But, how does one cite this pharmaceutical gem in AMA (American Medical
      Association) referencing style?
    </p>
    <p>
      <strong>Citing a drug monograph from a print source.</strong> The general
      structure to follow when citing a drug monograph from a print source is:
    </p>
    <CitationBlockMini>
      Author(s). Title of monograph. In: Editor(s), ed. Title of Book. Edition.
      Location: Publisher; Year: Page range.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J. Acetaminophen: A Comprehensive Guide. In: Jones R, ed. Handbook
      of Non-Prescription Drugs. 5th ed. Chicago, IL: Medical Press; 2021:
      100-120.
    </CitationExampleBox>
    <p>
      <strong>Citing a drug monograph from an online database.</strong> For an
      online source, you&apos;ll need to include the URL and the date of access:
    </p>
    <CitationBlockMini>
      Author(s). Title of monograph. In: Title of Database. URL. Accessed Month
      Day, Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Johnson P. Ibuprofen: A Clinical Overview. In: DrugInfo Database.{' '}
      <TextWithColor>https://www.druginfodb.com/ibuprofen.</TextWithColor>{' '}
      Accessed April 20, 2022.
    </CitationExampleBox>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Ensure that every aspect of the
      citation, including punctuation and capitalization, follows AMA style. The
      clarity in citation reflects the precision in research.
    </p>
    <p>
      Citing drug monographs in AMA style might seem like a complex chemical
      equation at first glance. However, with this guide, the process becomes as
      straightforward as a doctor&apos;s prescription.
    </p>
  </GuideSection>
);

const FigureGraphPictureCitations: React.FC = () => (
  <GuideSection id="FigureGraphPicture">
    <GuideHeader>Cite Figures, Graphs &amp; Pictures in AMA</GuideHeader>
    <p>
      In the vast landscape of scientific research, figures, graphs, and
      pictures are the artistry that adds color, clarity, and context.
      They&apos;re not just decorative pieces; they are critical elements that
      convey data, support findings, and often, tell a story that words alone
      can&apos;t capture. Citing these visual components appropriately is
      essential in the AMA (American Medical Association) referencing style, not
      just to give credit but to enhance understanding.
    </p>
    <p>
      So, how do you turn these visual masterpieces into properly cited works of
      scholarly art? Let&apos;s dive into this vibrant world and explore the
      nuances of citing figures, graphs, and pictures in AMA style.
    </p>
    <p>
      <strong>Citing a figure, graph, or picture.</strong> In AMA style, when
      you include a figure, graph, or picture from a published source, you need
      to reference it properly in your list of references. Here’s the general
      structure:
    </p>
    <CitationBlockMini>
      Author(s). Title of article. Journal Name. Year;volume(issue):page range.
      Figure/Table Number.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J, Johnson A. The effect of sunlight on plant growth. Botany
      Studies. 2020;12(4):321-330. Figure 3.
    </CitationExampleBox>
    <p>
      <strong>Citing an online image.</strong> For images found online, include
      the URL and the date of access:
    </p>
    <CitationBlockMini>
      Author(s) or Organization. Title of work. Website Name. Published date.
      URL. Accessed Day Month Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      National Weather Service. Hurricane paths 2020. NOAA. Published 2021.
      https://www.noaa.gov/hurricanes. Accessed 4 April 2022.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        <strong>No author</strong>: If the image doesn&apos;t have a specific
        author, start with the title.
      </li>
      <li>
        <strong>Original artwork</strong>: If the figure or image is your own,
        it&apos;s not necessary to cite it in the reference list, but you may
        want to note it in the caption.
      </li>
      <li>
        <strong>Images from databases</strong>: Include database name and URL if
        necessary for clear identification.
      </li>
    </SingleSpacedList>
    <p>
      <strong>Pro Tip: Keep the Context Clear</strong>
    </p>
    <p>
      A picture might be worth a thousand words, but without proper context, its
      meaning can be lost. Ensure that each cited image is relevant to your text
      and clearly labeled with a caption that includes the figure number, title,
      and any necessary explanatory notes.
    </p>
    <p>
      Citing figures, graphs, and pictures in AMA style is more than a mere
      formality; it&apos;s a way to intertwine visual elements with text,
      enriching the narrative and illuminating the data. This guide offers a
      palette of citation techniques tailored to various types of visual
      content, enabling you to paint a vivid, compelling, and ethically sound
      picture in your scholarly work.
    </p>
  </GuideSection>
);
const PatentCitations: React.FC = () => (
  <GuideSection id="Patents">
    <GuideHeader>Cite Patents in AMA</GuideHeader>
    <p>
      Patents are the epitome of innovation, guarding the intellectual treasures
      that lie within scientific advancements. They play a critical role not
      only in safeguarding inventions but also in fostering further research and
      development. In the world of academic writing, citing patents properly
      adds a level of authenticity and richness to your work, showing engagement
      with primary sources of innovation. AMA (American Medical Association)
      referencing style offers a specific pathway for citing patents, ensuring
      the inventiveness within the pages is duly credited.
    </p>
    <p>
      <strong>Citing a patent.</strong> In AMA style, the format for citing a
      patent is:
    </p>
    <CitationBlockMini>
      Inventor(s). Title of patent. Country granting the patent. Patent number.
      Day Month Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Johnson AB, Smith CD. Apparatus for measuring blood pressure. US patent US
      8,123,456. 15 March 2010.
    </CitationExampleBox>

    <p>
      <strong>Citing an online patent.</strong> If you are referring to a patent
      found online, you would add the URL and the date of access:
    </p>
    <CitationBlockMini>
      Inventor(s). Title of patent. Country granting the patent. Patent number.
      Day Month Year. URL. Accessed Day Month Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Thomas EF. Device for glucose monitoring. US patent US 9,234,567. 22 June
      2015. https://www.uspto.gov/patents-application. Accessed 1 September
      2022.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        <strong>Corporate author</strong>: If the inventor is an organization or
        company, you can list it as the author.
      </li>
      <li>
        <strong>Pending patents</strong>: If the patent is pending, you may
        write &quot;patent pending&quot; instead of the patent number.
      </li>
    </SingleSpacedList>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Prioritize precision. When citing
      a patent, precision matters. Ensure that the patent number, country, and
      other details are accurately noted. Misrepresentation might lead to
      confusion and reduce the credibility of your work.
    </p>
    <p>
      Whether you&apos;re exploring groundbreaking medical devices or novel
      pharmaceutical compounds, knowing how to cite patents adds a distinctive
      layer of authority to your academic writing. With this guide in hand, you
      are now equipped to navigate the fascinating world of patents, translating
      legal documents into scholarly citations that enrich your work and pay
      homage to the inventors&apos; effort.
    </p>
  </GuideSection>
);
const DrugCitations: React.FC = () => (
  <GuideSection id="Drugs">
    <GuideHeader>Cite Drug Information in AMA</GuideHeader>
    <p>
      In the world of healthcare, every piece of information counts, including
      the fine print on medical packaging and the specific details in
      prescribing information. These may seem like mere technicalities, but
      they&apos;re essential in understanding the properties, usage,
      indications, and contraindications of pharmaceutical products. When
      you&apos;re writing a research paper, thesis, or article in the medical
      field, citing these sources is crucial for accuracy and integrity.
      Let&apos;s delve into how to cite package inserts and prescribing
      information in AMA (American Medical Association) referencing style.
    </p>
    <p>
      <strong>Citing a package insert.</strong> A package insert contains
      detailed information about a medication, and it&apos;s often used as a
      reference in research. Here&apos;s how to cite it:
    </p>
    <CitationBlockMini>
      Manufacturer. Title of package insert. Location: Publisher; Year.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Pfizer Inc. Zithromax (azithromycin) package insert. New York, NY: Pfizer
      Inc.; 2020.
    </CitationExampleBox>
    <p>
      <strong>Citing prescribing information.</strong> Prescribing information
      is a detailed document that provides comprehensive details about a drug.
      Here&apos;s the proper way to cite it:
    </p>
    <CitationBlockMini>
      Manufacturer. Title of prescribing information. In: Product Name
      [prescribing information]. Location: Publisher; Year: Page(s).
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Johnson & Johnson. Tylenol (acetaminophen) prescribing information. In:
      Tylenol [prescribing information]. New Brunswick, NJ: Johnson & Johnson;
      2019:12-16.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        <strong>For digital inserts or information</strong>: If the package
        insert or prescribing information is obtained from an online source, you
        must include the URL at the end of the citation.
      </li>
      <li>
        <strong>For generic medications</strong>: If you&apos;re citing
        information related to a generic medication, you might include the
        generic name in brackets after the brand name.
      </li>
    </SingleSpacedList>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> When citing package inserts and
      prescribing information, it&apos;s essential to include as much detail as
      possible, as these documents are often updated. Including the publication
      year will help readers track down the specific version you referred to.
    </p>
  </GuideSection>
);

const PersonalCommunicationCitations: React.FC = () => (
  <GuideSection id="PersonalCommunication">
    <GuideHeader>Cite Personal Communications in AMA</GuideHeader>
    <p>
      In the hustle and bustle of research and professional discourse, we often
      engage in personal communications that spark insights or uncover valuable
      information. These might be private emails, conversations, interviews, or
      even a quick chat over coffee.
    </p>
    <p>
      While these exchanges might be less formal than a peer-reviewed paper or
      published book, they often hold gems that can enrich your work.
    </p>
    <p>
      So, how do you cite these unrecorded whispers of wisdom in AMA (American
      Medical Association) referencing style? This guide will help you honor the
      voices behind the insight while adhering to the standards of scholarly
      writing.
    </p>
    <p>
      <strong>General Format for Citing Personal Communications</strong>
    </p>
    <p>
      In AMA style, personal communications are cited within the text rather
      than in the reference list, as they are not retrievable by others. The
      general format includes the communicator&apos;s initials and last name,
      the phrase &quot;written (or oral) communication,&quot; and the exact
      date.
    </p>
    <p>
      <strong>Example:</strong>
    </p>
    <p>
      According to J. Smith{' '}
      <IntextCitation>(written communication, March 3, 2020)</IntextCitation>,
      the drug&apos;s effect is more potent in higher concentrations.
    </p>
    <p>
      <strong>Or</strong>
    </p>
    <p>
      The patient&apos;s symptoms improved after the therapy, as explained in a
      conversation with Dr. A. Johnson{' '}
      <IntextCitation>(oral communication, April 15, 2021)</IntextCitation>.
    </p>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        <strong>No specific date</strong>: If you don&apos;t remember the exact
        date of the communication, you can use the month and year or just the
        year if that&apos;s all you recall.
      </li>
      <li>
        <strong>Email communication</strong>: For emails, you can follow the
        same structure, specifying it as &quot;email communication.&quot;
      </li>
    </SingleSpacedList>
    <p>
      <strong>Pro Tip: Seek Permission</strong>
    </p>
    <p>
      It&apos;s considered professional and ethical to seek permission from the
      person with whom you had the personal communication before citing them in
      your work. This helps maintain trust and ensures that you are accurately
      representing their views.
    </p>
    <p>
      Citing personal communications in AMA style is like capturing fleeting
      thoughts and making them tangible for your readers. By including these
      insightful moments in your work, you&apos;re adding depth, context, and a
      human touch to your academic writing. We hope this guide helps you
      navigate this somewhat uncharted territory, providing the tools to
      properly attribute the voices that have shaped your work.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection id="FinalNotes">
    <GuideHeader>AMA Style: Final Notes</GuideHeader>
    <p>
      Navigating the diverse landscape of AMA citations doesn&apos;t have to be
      a daunting task. Whether you&apos;re a student, researcher, or
      professional, understanding the rules for properly referencing various
      sources in AMA style enhances the credibility and integrity of your work.
      From journal articles to personal communications, and even more
      specialized sources like drug monographs and patents, this guide has
      offered a comprehensive look at the nuances of citing within the AMA
      referencing framework.
    </p>
    <p>
      In today&apos;s digital age, the ease of creating citations has been
      revolutionized by tools such as citation machines. Platforms like{' '}
      Bibcitation offer intuitive solutions that enable you to create citations
      in AMA style effortlessly. Whether you need to reference figures, graphs,
      or engage with more complex referencing tasks, a citation machine is a
      must-have tool in your academic toolkit. The ability to generate precise
      and consistent citations enhances the readability and professionalism of
      your scholarly writing.
    </p>
    <p>
      The AMA referencing style is an essential component of medical and
      scientific writing. By adhering to these guidelines, you ensure that your
      work aligns with the accepted standards of your field. Should you ever
      find yourself puzzled by the intricate rules, remember that tools designed
      to create citations are readily available at your fingertips. Equip
      yourself with a reliable citation machine and empower your writing with
      accurate, efficient, and hassle-free AMA citations. After all, proper
      referencing is not just about following rules; it&apos;s about honoring
      the intellectual endeavors that have contributed to your own understanding
      and knowledge.
    </p>
  </GuideSection>
);

export default AmaGuide;
